import { useCallback } from 'react'
import { persistor, resetApp, useAppDispatch } from '../store'
import { setUser } from '../store/slices/userSlice'
import { setAccount } from '../store/slices/accountSlice'
import { normalizeAuthSession } from '../store/normalizeAuthSession'
import type { AuthSessionDto } from '../types/auth'
import { Sentry } from '../sentry'
import {
  clearYmIdentity,
  reachGoal,
  setMtrContractNumber,
  setYmUserId,
  setYmUserParams,
  setYmVisitParams,
} from '../ym'

export type AuthInitSource = 'session' | 'contract' | 'telegram' | 'call' | 'register'

/**
 * Отсеивает sentinel `0` (из normalizeAuthSession для отсутствующего договора),
 * NaN, негативные и нечисловые значения. Возвращает digits-only строку.
 */
function normalizeYmContract(raw: number | null | undefined): string | undefined {
  if (typeof raw !== 'number' || !Number.isFinite(raw) || raw <= 0) return undefined
  const s = String(raw).replace(/\D/g, '')
  return s.length > 0 ? s : undefined
}

/**
 * useAuthInit — pure store orchestrator.
 * Does NOT make API calls. Receives auth response from login/session/complete
 * endpoints, нормализует через normalizeAuthSession и пишет в Redux store.
 *
 * Используй initSession(dto) для всех auth flow'ов. Старый init(user, account)
 * удалён — он маскировал shape mismatches между API DTO и Store types через
 * `as any` касты в callsites.
 */
export function useAuthInit() {
  const dispatch = useAppDispatch()

  /**
   * Принимает любой auth DTO (telegram-complete, call-complete, register,
   * session). Нормализует и dispatch'ит. Single entry point.
   *
   * Дополнительно шлёт в Яндекс.Метрику три канала идентификации, чтобы
   * саппорт мог найти сессию клиента в вебвизоре по номеру договора:
   * 1. setUserID(user.id) — cross-device spine (не трогаем).
   * 2. userParams — сайдбар "Параметры посетителя" в вебвизоре. Retroactive:
   *    после первого вызова прошлые сессии этого user.id тоже получат contract.
   * 3. visit params — sidebar "Параметры визита", frozen per-visit. Главный
   *    надёжный канал фильтрации в вебвизоре (не подвержен retroactivity).
   * Goal params с договором прикрепляются к auth_success, auth_session_restored
   * и register_success — для быстрых goal-breakdowns в отчётах.
   *
   * TODO (multi-contract): если появится UI-переключатель контракта, этот
   * блок надо вынести в отдельный хелпер и вызывать ещё и на
   * dispatch(setAccount(newAccount)). Сейчас предполагаем user↔contract 1:1
   * на протяжении жизни сессии.
   */
  const initSession = useCallback((dto: AuthSessionDto, source: AuthInitSource = 'session') => {
    const { user, account } = normalizeAuthSession(dto)
    dispatch(setUser(user))
    dispatch(setAccount(account))

    const contract = normalizeYmContract(account?.contractNumber ?? null)

    // mtr_ base-params: закешировать contract_number для reachMtrGoal.
    setMtrContractNumber(contract ?? null)

    // v2: Sentry.setUser — привязка events к клиенту для support lookup.
    // ТОЛЬКО id (design doc Codex R2 #1, PII-minimal). Phone/email берутся
    // backend'ом из Prisma при resolve query — Sentry events хранят только
    // numeric id, support никогда не видит raw PII в Sentry UI.
    Sentry.setUser({ id: String(user.id) })

    setYmUserId(user.id)

    // userParams — пустая строка явно затирает старый договор (на случай
    // если юзер когда-то имел contract, а теперь перерегистрирован без).
    setYmUserParams({
      UserID: String(user.id),
      account: { contract_number: contract ?? '' },
    })

    // Visit params — шлём только когда договор реально есть; для юзеров
    // без договора корректно не иметь поля вообще, чем фиксировать пустое
    // значение как "у визита был пустой договор".
    if (contract) {
      setYmVisitParams({ account: { contract_number: contract } })
    }

    if (source === 'register') {
      reachGoal('register_success', {
        hasAccount: Boolean(account),
        account: { contract_number: contract },
      })
      return
    }

    if (source === 'session') {
      reachGoal('auth_session_restored', {
        hasAccount: Boolean(account),
        account: { contract_number: contract },
      })
      return
    }

    reachGoal('auth_success', {
      method: source,
      hasAccount: Boolean(account),
      account: { contract_number: contract },
    })
  }, [dispatch])

  const reset = useCallback(async () => {
    reachGoal('logout')
    // v2: logout → снять user с Sentry context чтобы post-logout ошибки не
    // приписывались выходящему юзеру (auth state change race — минимальный
    // window микросекунд, но корректный invariant).
    Sentry.setUser(null)
    clearYmIdentity()
    setMtrContractNumber(null)
    dispatch(resetApp())

    // redux-persist хранит user/account между сессиями. После logout надо
    // снести snapshot целиком, иначе новый логин может кратко увидеть старые
    // persisted данные до прихода свежих ответов.
    await persistor.purge()
  }, [dispatch])

  return { initSession, reset }
}
